let ctx = null;
let unlocked = false;

// Notification chimes were far too quiet — boost every tone's gain by this factor
// (clamped so summed oscillators don't clip). The per-wolf volume slider (0..1,
// saved on-device) scales on top of this. Bump CHIME_VOLUME for a louder ceiling.
const CHIME_VOLUME = 5;
export function getNotifVolume() {
  if (typeof localStorage === "undefined") return 1;
  const v = parseFloat(localStorage.getItem("ps_notif_volume"));
  return isNaN(v) ? 1 : Math.max(0, Math.min(1, v));
}
export function setNotifVolume(v) {
  const clamped = Math.max(0, Math.min(1, v));
  if (typeof localStorage !== "undefined") localStorage.setItem("ps_notif_volume", String(clamped));
}
const amp = (p) => Math.max(0.00001, Math.min(0.95, p * CHIME_VOLUME * getNotifVolume()));

function getCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  return ctx;
}

// Run cb with the audio context, ensuring it is (being) resumed.
// iOS Safari only lets audio play when it is scheduled SYNCHRONOUSLY inside the
// user-gesture call stack. Scheduling it later, inside a resume().then() callback,
// gets silently blocked on iOS. So we fire resume() (without awaiting) and then
// schedule the tone synchronously — a node scheduled while the context is briefly
// suspended still sounds once the gesture-triggered resume completes.
function withRunningCtx(cb) {
  const audio = getCtx();
  if (!audio) return;
  if (audio.state !== "running") {
    try { const p = audio.resume(); if (p && typeof p.catch === "function") p.catch(() => {}); } catch (e) { /* ignore */ }
  }
  try { cb(audio); } catch (e) { /* ignore */ }
}

// A soft two-note "howl" chime for incoming direct messages.
export function playHowlTone() {
  withRunningCtx((audio) => {
    const now = audio.currentTime;
    const master = audio.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(amp(0.18), now + 0.05);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 1.1);
    master.connect(audio.destination);

    const notes = [
      { freq: 523.25, start: 0, dur: 0.5 },   // C5
      { freq: 784.0, start: 0.18, dur: 0.7 }, // G5
    ];
    notes.forEach((n) => {
      const osc = audio.createOscillator();
      const g = audio.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(n.freq, now + n.start);
      osc.frequency.exponentialRampToValueAtTime(n.freq * 0.94, now + n.start + n.dur);
      g.gain.setValueAtTime(0.0001, now + n.start);
      g.gain.exponentialRampToValueAtTime(0.9, now + n.start + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, now + n.start + n.dur);
      osc.connect(g);
      g.connect(master);
      osc.start(now + n.start);
      osc.stop(now + n.start + n.dur + 0.05);
    });
  });
}

// Unlock audio on a real user gesture (iOS/Android/desktop autoplay policies).
export function primeAudio() {
  const audio = getCtx();
  if (!audio) return;
  if (audio.state === "suspended") audio.resume().catch(() => {});
  if (unlocked) return;
  try {
    const buffer = audio.createBuffer(1, 1, 22050);
    const src = audio.createBufferSource();
    src.buffer = buffer;
    src.connect(audio.destination);
    src.start(0);
    unlocked = true;
  } catch (e) { console.debug("audio unlock failed", e); }
}

// A short, soft chime. `enabling` true = rising note (turn on / new signal),
// false = a softer descending note (mute confirmation).
export function playSignalChime(enabling = true) {
  withRunningCtx((audio) => {
    const now = audio.currentTime;
    const osc = audio.createOscillator();
    const g = audio.createGain();
    osc.type = "sine";
    if (enabling) {
      osc.frequency.setValueAtTime(880, now);            // A5
      osc.frequency.exponentialRampToValueAtTime(1174.66, now + 0.12); // -> D6
    } else {
      osc.frequency.setValueAtTime(659.25, now);         // E5
      osc.frequency.exponentialRampToValueAtTime(440, now + 0.14);     // -> A4
    }
    const peak = enabling ? 0.12 : 0.07;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(amp(peak), now + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
    osc.connect(g);
    g.connect(audio.destination);
    osc.start(now);
    osc.stop(now + 0.45);
  });
}


// ---- Selectable notification chimes -----------------------------------------
// Each chime is generated procedurally (WebAudio) so there are no audio assets
// to ship. Wolves pick a DM chime and a Den chime in the sound settings.
function _tone(a, { f1, f2, type = "sine", t = 0, dur = 0.4, peak = 0.12 }) {
  const now = a.currentTime + t;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f1, now);
  if (f2) osc.frequency.exponentialRampToValueAtTime(f2, now + dur);
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(amp(peak), now + 0.03);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  osc.connect(g);
  g.connect(a.destination);
  osc.start(now);
  osc.stop(now + dur + 0.05);
}

const CHIME_DEFS = {
  howl: (a) => { _tone(a, { f1: 523.25, f2: 491, dur: 0.5, peak: 0.12 }); _tone(a, { f1: 784, f2: 737, t: 0.18, dur: 0.6, peak: 0.14 }); },
  ping: (a) => { _tone(a, { f1: 880, f2: 1174.66, dur: 0.4, peak: 0.12 }); },
  bell: (a) => { _tone(a, { f1: 1046.5, type: "triangle", dur: 0.7, peak: 0.1 }); _tone(a, { f1: 2093, type: "triangle", dur: 0.5, peak: 0.05 }); },
  chirp: (a) => { _tone(a, { f1: 600, f2: 1400, dur: 0.18, peak: 0.12 }); },
  pulse: (a) => { _tone(a, { f1: 440, type: "square", dur: 0.1, peak: 0.06 }); _tone(a, { f1: 440, type: "square", t: 0.15, dur: 0.1, peak: 0.06 }); },
  drop: (a) => { _tone(a, { f1: 660, f2: 330, dur: 0.35, peak: 0.1 }); },
  welcome: (a) => { _tone(a, { f1: 392, type: "triangle", dur: 0.4, peak: 0.11 }); _tone(a, { f1: 523.25, type: "triangle", t: 0.13, dur: 0.4, peak: 0.12 }); _tone(a, { f1: 659.25, type: "triangle", t: 0.26, dur: 0.5, peak: 0.13 }); },
  none: () => {},
};

export const CHIMES = [
  { id: "howl", label: "Howl" },
  { id: "ping", label: "Ping" },
  { id: "bell", label: "Bell" },
  { id: "chirp", label: "Chirp" },
  { id: "pulse", label: "Pulse" },
  { id: "drop", label: "Drop" },
  { id: "welcome", label: "Welcome" },
  { id: "none", label: "Silent" },
];

export function playChimeById(id) {
  const def = CHIME_DEFS[id] || CHIME_DEFS.howl;
  withRunningCtx((audio) => def(audio));
}

export function getDmChime() { return localStorage.getItem("ps_chime_dm") || "howl"; }
export function setDmChime(id) { localStorage.setItem("ps_chime_dm", id); }
export function getDenChime() { return localStorage.getItem("ps_chime_den") || "ping"; }
export function setDenChime(id) { localStorage.setItem("ps_chime_den", id); }
export function getMateChime() { return localStorage.getItem("ps_chime_mate") || "welcome"; }
export function setMateChime(id) { localStorage.setItem("ps_chime_mate", id); }
export function playDmChime() { playChimeById(getDmChime()); }
export function playDenChime() { playChimeById(getDenChime()); }
export function playMateChime() { playChimeById(getMateChime()); }
